
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { EarlyAccessLead, EarlyAccessStats } from '@/types/early-access';
import { calculateStats } from '@/utils/statsCalculator';

export const useEarlyAccessLeads = () => {
  const [leads, setLeads] = useState<EarlyAccessLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [stats, setStats] = useState<EarlyAccessStats>({
    total: 0,
    today: 0,
    thisWeek: 0,
    interviewInterested: 0
  });
  const { toast } = useToast();

  const updateStats = (data: EarlyAccessLead[]) => {
    const newStats = calculateStats(data);
    console.log('Stats calculated:', newStats);
    setStats(newStats);
  };

  const fetchLeads = async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) setRefreshing(true);
      
      console.log('Fetching leads from database...');
      
      const { data, error, count } = await supabase
        .from('early_access_leads')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      console.log('Query result:', { data, error, count });
      console.log('Total records found:', data?.length || 0);

      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }

      setLeads(data || []);
      updateStats(data || []);
      
      if (showRefreshToast) {
        toast({
          title: "Datos actualizados",
          description: `Se cargaron ${data?.length || 0} registros`,
        });
      }
      
      console.log('Leads state updated with:', data?.length || 0, 'records');
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({
        title: "Error",
        description: "Error al cargar los registros",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const deleteLead = async (id: string) => {
    setDeleting(id);
    try {
      console.log('Deleting lead with id:', id);
      const { error } = await supabase
        .from('early_access_leads')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Registro eliminado correctamente",
      });
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast({
        title: "Error",
        description: "Error al eliminar el registro",
        variant: "destructive"
      });
    } finally {
      setDeleting(null);
    }
  };

  const deleteAllLeads = async () => {
    setDeletingAll(true);
    try {
      console.log('Deleting all leads...');
      const { error } = await supabase
        .from('early_access_leads')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Todos los registros han sido eliminados",
      });
    } catch (error) {
      console.error('Error deleting all leads:', error);
      toast({
        title: "Error",
        description: "Error al eliminar todos los registros",
        variant: "destructive"
      });
    } finally {
      setDeletingAll(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    
    // Set up realtime subscription for automatic updates
    console.log('Setting up realtime subscription...');
    const channel = supabase
      .channel('early-access-leads-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'early_access_leads'
        },
        (payload) => {
          console.log('Realtime event received:', payload);
          
          if (payload.eventType === 'INSERT') {
            const newLead = payload.new as EarlyAccessLead;
            console.log('New lead added:', newLead);
            
            setLeads(currentLeads => {
              const updatedLeads = [newLead, ...currentLeads];
              updateStats(updatedLeads);
              return updatedLeads;
            });
            
            toast({
              title: "Nuevo registro",
              description: `Se ha añadido un nuevo registro de ${newLead.first_name}`,
            });
          } else if (payload.eventType === 'DELETE') {
            const deletedId = payload.old.id;
            console.log('Lead deleted:', deletedId);
            
            setLeads(currentLeads => {
              const updatedLeads = currentLeads.filter(lead => lead.id !== deletedId);
              updateStats(updatedLeads);
              return updatedLeads;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedLead = payload.new as EarlyAccessLead;
            console.log('Lead updated:', updatedLead);
            
            setLeads(currentLeads => {
              const updatedLeads = currentLeads.map(lead => 
                lead.id === updatedLead.id ? updatedLead : lead
              );
              updateStats(updatedLeads);
              return updatedLeads;
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to realtime updates');
        }
      });

    // Auto-refresh every 60 seconds as backup
    const interval = setInterval(() => {
      console.log('Auto-refresh triggered');
      fetchLeads();
    }, 60000);

    return () => {
      console.log('Cleaning up realtime subscription and interval');
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  return {
    leads,
    stats,
    loading,
    refreshing,
    deleting,
    deletingAll,
    fetchLeads,
    deleteLead,
    deleteAllLeads
  };
};
